import { useState } from "react";
import HelpFAB from "@/components/shell/help-fab";
import Sidebar from "@/components/shell/sidebar";
import Topbar from "@/components/shell/topbar";
import type { NavSection } from "@/lib/constants";

export default function AppShell({
  children,
  navSections,
}: {
  children: React.ReactNode;
  navSections: NavSection[];
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  function toggleCollapsed() {
    setCollapsed((prev) => !prev);
  }

  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      {/* Below lg the sidebar becomes an off-canvas drawer; at lg+ it matches the finalized shell. */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div
        className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 lg:static lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <Sidebar
          sections={navSections}
          onNavigate={() => setSidebarOpen(false)}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapsed}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden p-0 md:p-3">
        <div
          data-testid="app-container"
          className="flex min-h-0 flex-1 flex-col overflow-hidden border-line bg-surface md:rounded-[16px] md:border"
        >
          {/* The drawer toggle lives inside the one header row (`Topbar`), as
              the design reference has it. It used to be a standalone bar
              rendered above this panel, which stacked a second grey bar over
              the real topbar on a phone. */}
          <Topbar menuOpen={sidebarOpen} onToggleMenu={() => setSidebarOpen((open) => !open)} />
          <main data-testid="app-content" className="min-h-0 flex-1 overflow-y-auto">
            <div className="w-full px-4 pb-12 pt-6 md:px-6 md:pb-14 md:pt-7">{children}</div>
          </main>
        </div>
      </div>
      <HelpFAB />
    </div>
  );
}
